<template>
  <v-container
    fluid
    class="px-0 mx-0 pt-0"
  >
    <v-layout
      row
      wrap
    >
      <v-flex xs12>
        <v-tabs
          ref="tabs"
          v-model="selectedTabIndex"
          show-arrows
          :color="options.color"
          :background-color="options.tabColor"
        >
          <v-tabs-slider color="yellow" />
          <v-tab
            v-for="tab in tabs"
            :key="tab.name"
            light
            @click="activateTab(tab)"
          >
            {{ tab.text }}
          </v-tab>
          <v-tab-item>
            <v-app-bar
              ref="toolbar"
              flat
              dense
              class="elevation-1 pt-3"
            >
              <v-layout>
                <v-flex
                  sm11
                  d-flex
                >
                  <component
                    :is="filter"
                    ref="filterComponent"
                    @changed="filterChanged"
                  />
                </v-flex>
                <v-flex
                  sm1
                  d-flex
                >
                  <v-btn
                    :block="true"
                    @click="resetFilter"
                  >
                    {{ $contentTransform.translate($contentTransform.lang.CLEAN) }}
                  </v-btn>
                </v-flex>
              </v-layout>
            </v-app-bar>
            <v-layout
              v-if="areThereSelectedItems()"
              class="pl-5"
            >
              <h3 class="list-title">
                {{ $t($lang.content.SELECTED_CONTENTS) }}
              </h3>
            </v-layout>
            <v-list
              two-line
              class="content-list"
            >
              <template v-for="(content, contentIndex) in selectedContents">
                <slot
                  name="content"
                  :content="content"
                  :index="contentIndex"
                  :selected-contents="selectedContents"
                  :click="contentClick"
                  :edit="contentEdit"
                  :is-item-selected="checkIsItemSelected"
                />
              </template>
            </v-list>
            <v-layout class="pl-5">
              <h3 class="list-title">
                {{ $t($lang.content.CONTENTS) }}
              </h3>
            </v-layout>
            <v-list
              two-line
              class="content-list"
            >
              <template v-for="(content, contentIndex) in contents">
                <slot
                  name="content"
                  :content="content"
                  :index="contentIndex"
                  :selected-contents="selectedContents"
                  :click="contentClick"
                  :edit="contentEdit"
                  :is-item-selected="checkIsItemSelected"
                />
              </template>
              <v-layout
                v-if="isShowMoreButtonEnabled"
                class="text-xs-center justify-center ma-5"
              >
                <v-btn
                  rounded
                  dark
                  color="default"
                  @click="showMore"
                >
                  {{ $contentTransform.translate($contentTransform.lang.SHOW_MORE) }}
                </v-btn>
              </v-layout>
            </v-list>
          </v-tab-item>
        </v-tabs>
      </v-flex>
    </v-layout>
    <slot
      name="edit"
      :edit-form-model="editFormModel"
      :is-editing="isEditing"
      :save="contentSave"
      :cancel="contentEditCancel"
    />
  </v-container>
</template>

<script>


import { clone, mergeDeepLeft } from 'ramda';
import AlertError from '../../plugins/piri/error/types/AlertError';

function createTabs(vm, providers) {
  vm.tabs.splice(0);

  providers.forEach((provider, index) => {
    if (!provider.name) {
      throw new Error('Provider must have a name.');
    }

    const tab = {
      name: provider.name,
      text: provider.text,
      index,
      provide: null,
      transformer: null,
      filter: null,
    };

    if (provider.provide) {
      tab.provide = provider.provide;
    } else if (vm.$contentTransform.modules[provider.name]) {
      tab.provide = vm.$contentTransform.modules[provider.name].provide;
    }

    if (tab.provide) {
      tab.transformer = vm.$contentTransform.modules[provider.name].transformers[vm.contentType];
      tab.filter = vm.$contentTransform.modules[provider.name].Filter;
      vm.tabs.push(tab);
    } else {

      console.error(`${tab.name}(${index}.) tab is incorrect. Please check provider.`);
    }
  });
}

function providerNotFoundAlert(vm) {
  vm.$root.$alert(
    vm.$contentTransform.translate(vm.$contentTransform.lang.WARNING),
    vm.$contentTransform.translate(vm.$contentTransform.lang.CONTENT_PROVIDER_NOT_FOUND),
    { color: 'warning' },
  );
}

function transformerNotFoundAlert(vm) {
  vm.$root.$alert(
    vm.$contentTransform.translate(vm.$contentTransform.lang.WARNING),
    vm.$contentTransform.translate(vm.$contentTransform.lang.CONTENT_TRANSFORMER_NOT_FOUND),
    { color: 'warning' },
  );
}

const askIfUserApprovesToCancelSelection = vm => vm.$root.$confirm(
  vm.$contentTransform.translate(vm.$contentTransform.lang.SELECTED_ITEM_FOUND),
  vm.$contentTransform.translate(vm.$contentTransform.lang.SELECTED_CONTENTS_WILL_BE_LOST),
  { color: 'warning' },
);

const askIfUserApprovesToCancelEdit = vm => vm.$root.$confirm(
  vm.$contentTransform.translate(vm.$contentTransform.lang.EDITED_ITEM_FOUND),
  vm.$contentTransform.translate(vm.$contentTransform.lang.EDITED_CONTENTS_WILL_BE_LOST),
  { color: 'warning' },
);

const askClosePopupToLoseChanges = vm => vm.$root.$confirm(
  vm.$contentTransform.translate(vm.$contentTransform.lang.CHANGES_FOUND),
  vm.$contentTransform.translate(vm.$contentTransform.lang.CHANGES_WILL_BE_LOST),
  { color: 'warning' },
);

function resetSelectedTab(vm) {
  vm.page = 1;
  vm.isAnyContentEdited = false;
}

function clearContentList(contents) {
  contents.splice(0);
}

function mergeContents(vm, allContents) {
  vm.contents.splice(0, vm.contents.length, ...allContents);
}

function hideShowMoreButton(vm) {
  vm.isShowMoreButtonEnabled = false;
}

function clearFilter(vm) {
  if (vm.$refs.filterComponent) {
    vm.$refs.filterComponent.reset();
  }

  vm.filterOptions = {};
}

export default {
  name: 'ContentTransformComponent',
  props: {
    contentType: {
      type: String,
      default: '',
    },
    size: {
      type: Number,
      default: 1,
    },
    options: {
      type: Object,
      default: () => ({
        color: 'white',
        tabColor: 'cyan',
      }),
    },
  },
  data() {
    return {
      providers: [],
      tabs: [],
      selectedTab: {},
      selectedTabIndex: 0,
      page: 1,
      filter: null,
      filterOptions: {},
      contents: [],
      selectedContents: [],
      isAnyContentEdited: false,
      isShowMoreButtonEnabled: false,
      isEditing: false,
      editFormModel: {},
      editingItem: undefined,
      editingItemIndex: undefined,
    };
  },
  computed: {
    itemIdentifier() {
      return (
        this.$contentTransform.itemIdentifiers[this.contentType]
        || (() => {
          throw new AlertError(
            this.$contentTransform.translate(
              this.$contentTransform.lang.CONTENT_IDENTIFIER_NOT_FOUND,
            ),
          );
        })
      );
    },
  },
  methods: {
    initializeComponent(providers) {
      this.providers = providers;
      createTabs(this, this.providers);
      this.selectedTabIndex = 0;

      if (this.tabs.length > 0) {
        this.loadNewTab(this.tabs[0]);
      } else {
        hideShowMoreButton(this);
        providerNotFoundAlert(this);
      }
    },
    async activateTab(tab) {
      if (this.selectedTab.index === tab.index) {
        return;
      }

      if (
        (this.areThereSelectedItems() && !(await askIfUserApprovesToCancelSelection(this)))
        || (this.isAnyContentEdited && !(await askIfUserApprovesToCancelEdit(this)))
      ) {
        this.selectedTabIndex = this.selectedTab.index;

        return;
      }

      this.loadNewTab(tab);
    },
    loadNewTab(tab) {
      this.selectedContents.splice(0);
      resetSelectedTab(this);
      this.filter = tab.filter;
      clearFilter(this);
      this.loadTab(tab);
    },
    loadTab(tab) {
      this.selectedTab = tab;

      if (!tab.provide) {
        providerNotFoundAlert(this);
        hideShowMoreButton(this);
        clearContentList(this.contents);

        return false;
      }

      if (!tab.transformer) {
        transformerNotFoundAlert(this);
        hideShowMoreButton(this);
        clearContentList(this.contents);

        return false;
      }

      this.loadDataFromProvider(this.selectedTab);

      return true;
    },
    showMore() {
      this.page += 1;
      this.loadDataFromProvider(this.selectedTab);
    },
    loadDataFromProvider(tab) {
      this.$contentTransform.onRetrieveData(this);
      const options = {
        page: this.page,
        size: this.size,
        transformer: tab.transformer,
        queryObject: this.filterOptions,
      };

      tab
        .provide(this.contentType, this, options)
        .then(response => {
          const allContents = this.page === 1 ? response : (this.contents || []).concat(response);
          mergeContents(this, allContents);
          this.isShowMoreButtonEnabled = response.length >= this.size;
          this.$contentTransform.afterRetrieveData(this);

          return allContents;
        })
        .catch(() => {
          this.$contentTransform.onError(this);
        });
    },
    filterChanged(model) {
      this.filterOptions = model;
      this.applyFilter();
    },
    resetFilter() {
      clearFilter(this);
      this.applyFilter();
    },
    applyFilter() {
      resetSelectedTab(this);
      this.loadDataFromProvider(this.selectedTab);
    },
    contentClick(item) {
      if (!this.checkIsItemSelected(item)) {
        this.selectedContents.push(item);
      } else {
        this.selectedContents.splice(this.selectedContents.indexOf(item), 1);
      }
    },
    contentEdit(content, contentIndex) {
      this.editingItemIndex = contentIndex;
      this.editingItem = content;
      this.editFormModel = clone(content);
      this.isEditing = true;
    },
    contentSave() {
      this.editingItem = mergeDeepLeft(this.editFormModel, this.editingItem);

      if (!this.checkIsItemSelected(this.editingItem)) {
        this.selectedContents.push(this.editingItem);
      } else {
        const selectedItemIndex = this.selectedContents.findIndex(
          content => this.itemIdentifier(content) === this.itemIdentifier(this.editingItem),
        );
        this.selectedContents.splice(selectedItemIndex, 1, this.editingItem);

        const editedItemIndex = this.contents.findIndex(
          content => this.itemIdentifier(content) === this.itemIdentifier(this.editingItem),
        );
        this.contents.splice(editedItemIndex, 1, this.editingItem);
      }

      this.editFormModel = {};
      this.editingItem = undefined;
      this.editingItemIndex = undefined;
      this.isEditing = false;
    },
    contentEditCancel() {
      this.isEditing = false;
      this.editFormModel = {};
      this.editingItem = undefined;
    },
    checkIsItemSelected(item) {
      return this.selectedContents.some(
        content => this.itemIdentifier(content) === this.itemIdentifier(item),
      );
    },
    areThereSelectedItems() {
      return this.selectedContents.length;
    },
    getSelectedContents() {
      return this.selectedContents;
    },
    async checkIfThereAreChanges() {
      return !(
        (this.areThereSelectedItems() || this.isAnyContentEdited)
        && !(await askClosePopupToLoseChanges(this))
      );
    },
    cancel() {
      hideShowMoreButton(this);
      clearContentList(this.contents);
    },
  },
};
</script>

<style scoped>
h3.list-title {
  width: 100%;
  padding: 10px;
  border-top: 1px solid #dadada;
  border-bottom: 1px solid #dadada;
}

.content-list {
  padding: 0 0 8px 0;
}
</style>
